/**
 * useWlTenantPlanBinding — hooks para o painel WL gerenciar plano + recorrências
 * de um tenant.
 *
 * IMPORTANTE — refator 2026-06-15:
 *   A seleção de plano agora reaproveita 100% o orquestrador
 *   `tenant_checkout_create_order` (via wrapper `wl_apply_tenant_plan`),
 *   garantindo que toda a regra de provisionamento, gateway, trial, add-ons e
 *   1ª cobrança seja aplicada — exatamente como se o tenant tivesse contratado
 *   pela tela `/tenant/plans`.
 *   As RPCs legadas (`wl_set_tenant_plan_binding`, `wl_clear_tenant_plan_binding`,
 *   `wl_list_plans_for_binding`, `wl_list_tenant_primary_eligible_subscriptions`)
 *   foram removidas da base.
 *
 *   A seção "Recorrências adicionais" foi preservada como utilidade manual.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";
import { dispatchCobrancaToProvider } from "@/lib/financial/dispatchCobranca";
import type {
  EligiblePlanV1,
  CheckoutOrderResult,
  RecurrenceInterval,
} from "@/hooks/tenant/useTenantPlansV2";

const log = createLogger("useWlTenantPlanBinding");


// ---------------------------------------------------------------------------
// Tipagens
// ---------------------------------------------------------------------------
export interface WlEligibleSubscription {
  id: string;
  descricao: string | null;
  valor_total: number;
  moeda: string;
  frequencia: string | null;
  status: string;
  proximo_vencimento: string | null;
  customer_id: string | null;
  customer_name: string | null;
  is_current_primary?: boolean;
}

export interface WlTenantExtraSubscription {
  id: string;
  assinatura_id: string;
  descricao: string | null;
  valor_total: number;
  moeda: string;
  frequencia: string | null;
  status: string;
  customer_name: string | null;
  notes: string | null;
  created_at: string;
}

export interface WlTenantPlanBinding {
  binding_id: string;
  subscription_plan_id: string | null;
  plan_name: string | null;
  gateway_slug: string | null;
  master_tenant_id: string | null;
  primary_assinatura_id: string | null;
  primary_descricao: string | null;
  primary_valor: number | null;
  primary_moeda: string | null;
  primary_frequencia: string | null;
  primary_status: string | null;
  primary_customer_name: string | null;
  // v1.1.0 (2026-07-21): Meio de pagamento e conta corrente resolvidos
  // a partir da recorrência primária (financeiro_assinaturas).
  primary_meio_pagamento: string | null;
  primary_conta_corrente: string | null;
  extras: WlTenantExtraSubscription[];
  // v1.2.0 (2026-08-18): periodicidade contratada no binding V1
  // (mensal | trimestral | semestral | anual). Usada para filtrar add-ons
  // compatíveis com o ciclo do plano no modal de add-ons.
  recurrence_interval: string | null;
}


// ---------------------------------------------------------------------------
// Chaves de cache compartilhadas
// ---------------------------------------------------------------------------
const bindingKey = (tenantId?: string | null) => ["wl-tenant-plan-binding", tenantId];

// ---------------------------------------------------------------------------
// 1) Lista de planos elegíveis (mesma forma da tela /tenant/plans)
// ---------------------------------------------------------------------------
export function useWlEligiblePlansV1(tenantId?: string | null, enabled = true) {
  return useQuery({
    queryKey: ["wl-eligible-plans-v1", tenantId],
    enabled: !!tenantId && enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<EligiblePlanV1[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_eligible_plans_v1" as never,
        { p_tenant_id: tenantId } as never,
      );
      if (error) {
        log.error("Erro listar planos WL", { error });
        throw error;
      }
      return ((data ?? []) as any[]).map((p) => ({
        ...p,
        features: Array.isArray(p.features) ? p.features : [],
        accepted_payment_methods: Array.isArray(p.accepted_payment_methods)
          ? (p.accepted_payment_methods as string[])
          : [],
        accepted_payment_methods_resolved: Array.isArray(p.accepted_payment_methods_resolved)
          ? p.accepted_payment_methods_resolved
          : [],
        recurrences: Array.isArray(p.recurrences) ? p.recurrences : [],
      })) as EligiblePlanV1[];
    },
  });
}

// ---------------------------------------------------------------------------
// 2) Recorrências adicionais elegíveis (mantida — utilidade manual)
// ---------------------------------------------------------------------------
export function useWlExtraEligibleSubscriptions(
  tenantId?: string | null,
  search?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["wl-extra-eligible-subs", tenantId, search ?? null],
    enabled: !!tenantId && enabled,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<WlEligibleSubscription[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_tenant_extra_eligible_subscriptions" as never,
        { p_tenant_id: tenantId, p_search: search?.trim() || null } as never,
      );
      if (error) throw error;
      return (data || []) as WlEligibleSubscription[];
    },
  });
}

// ---------------------------------------------------------------------------
// 3) Leitura consolidada do vínculo atual
// ---------------------------------------------------------------------------
export function useWlTenantPlanBinding(tenantId?: string | null) {
  return useQuery({
    queryKey: bindingKey(tenantId),
    enabled: !!tenantId,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<WlTenantPlanBinding | null> => {
      const { data, error } = await supabase.rpc("wl_get_tenant_plan_binding" as never, {
        p_tenant_id: tenantId,
      } as never);
      if (error) throw error;
      const arr = (data ?? []) as any[];
      const row = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
      if (!row) return null;
      return {
        ...row,
        extras: Array.isArray(row.extras) ? (row.extras as WlTenantExtraSubscription[]) : [],
      } as WlTenantPlanBinding;
    },
  });
}

// ---------------------------------------------------------------------------
// 4) Mutações
// ---------------------------------------------------------------------------
export interface WlApplyPlanPayload {
  plan_id: string;
  recurrence_interval: RecurrenceInterval;
  payment_method: string;
  addons?: Array<{ addon_id: string; quantity: number }>;
  /**
   * Decisão do operador WL ao trocar plano de tenant com assinatura ativa:
   *  - 'immediate'    → aplica agora (força via p_force_apply na sub-RPC)
   *  - 'await_payment'→ grava pending_change e aguarda compensação (default)
   * Ignorado em 1ª contratação (roteado via tenant_checkout_create_order).
   */
  apply_mode?: "immediate" | "await_payment";
}

export function useWlTenantPlanBindingMutations(tenantId?: string | null) {
  const qc = useQueryClient();

  const invalidate = () => {
    // Binding e elegibilidade (chaves originais)
    qc.invalidateQueries({ queryKey: bindingKey(tenantId) });
    qc.invalidateQueries({ queryKey: ["wl-extra-eligible-subs", tenantId] });
    qc.invalidateQueries({ queryKey: ["wl-eligible-plans-v1", tenantId] });

    // Header + abas do WlTenantDetailsModal (chaves reais com UNDERSCORE
    // usadas em src/hooks/wl/useWlTenantDetails.ts). Sem isso, o header
    // continua exibindo status/plano antigos após aplicar plano.
    qc.invalidateQueries({ queryKey: ["wl_tenant_overview", tenantId] });
    qc.invalidateQueries({ queryKey: ["wl_tenant_billing_summary", tenantId] });
    qc.invalidateQueries({ queryKey: ["wl_tenant_billing_history", tenantId] });
    qc.invalidateQueries({ queryKey: ["wl_tenant_addons", tenantId] });
    qc.invalidateQueries({ queryKey: ["wl_tenant_members", tenantId] });

    // Listagem /wl/tenants (refletir status/plano na linha de origem)
    qc.invalidateQueries({ queryKey: ["wl-tenants-list"] });
  };

  /**
   * Aplica plano em nome do tenant reaproveitando o mesmo orquestrador da
   * contratação direta (tenant_checkout_create_order via wl_apply_tenant_plan).
   */
  const applyPlan = useMutation({
    mutationFn: async (payload: WlApplyPlanPayload): Promise<CheckoutOrderResult> => {
      // ETAPA 1 — RPC wrapper WL que chama tenant_checkout_create_order no master.
      // Cria binding + assinatura + ciclos (1º ciclo 'pendente', demais 'aguardando_emissao').
      const { data, error } = await supabase.rpc("wl_apply_tenant_plan" as never, {
        p_tenant_id: tenantId,
        p_payload: {
          plan_id: payload.plan_id,
          recurrence_interval: payload.recurrence_interval,
          payment_method: payload.payment_method,
          addons: payload.addons ?? [],
          // apply_mode é lido apenas no caminho "troca de plano" (wl_apply_tenant_plan v1.2.0).
          // Em 1ª contratação o backend ignora silenciosamente.
          apply_mode: payload.apply_mode ?? "await_payment",
        },
      } as never);
      if (error) throw error;
      const result = data as unknown as CheckoutOrderResult;

      // Curto-circuitos: mudança agendada / pré-pago aguardando pagamento
      // não emite nada aqui — quem despacha é o fluxo de compensação.
      if (result?.change_scheduled || result?.requires_payment) return result;

      // ETAPA 2a — 1ª cobrança (primeira contratação): despacha via helper
      // único compartilhado com /tenant/plans. Mantém comportamento atual.
      if (result?.first_cobranca_id) {
        // v2.0.0 (2026-06-17): helper enxuto — backend usa banco como fonte
        // de verdade (cobranca_id). Garante paridade local↔Asaas.
        await dispatchCobrancaToProvider({
          cobranca_id: result.first_cobranca_id,
          conta_corrente_id: result.conta_corrente_id,
          crm_customer_id: result.crm_customer_id,
          tipo_pagamento: result.tipo_pagamento,
          origin: "wl_first_charge",
        });
      }

      // ETAPA 2b — Troca imediata: a RPC v2.4.5 expõe `dispatch_charge` com
      // todos os campos da cobrança de ajuste pro-rata. Se presente,
      // despacha pelo mesmo helper. Sem essa etapa a cobrança ficava
      // eternamente em 'aguardando_emissao'.
      const dispatchCharge = (result as any)?.dispatch_charge;
      if (dispatchCharge?.cobranca_id) {
        await dispatchCobrancaToProvider({
          cobranca_id: dispatchCharge.cobranca_id,
          conta_corrente_id: dispatchCharge.conta_corrente_id,
          crm_customer_id: dispatchCharge.crm_customer_id,
          tipo_pagamento: dispatchCharge.tipo_pagamento ?? "pix",
          observacoes: dispatchCharge.observacoes ?? null,
          origin: dispatchCharge.origin ?? "wl_plan_change_pro_rata",
        });
      }

      return result;
    },
    onSuccess: invalidate,
  });


  const addExtra = useMutation({
    mutationFn: async (input: { assinaturaId: string; notes?: string | null }) => {
      const { data, error } = await supabase.rpc("wl_add_tenant_extra_subscription" as never, {
        p_tenant_id: tenantId,
        p_assinatura_id: input.assinaturaId,
        p_notes: input.notes ?? null,
      } as never);
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const removeExtra = useMutation({
    mutationFn: async (assinaturaId: string) => {
      const { data, error } = await supabase.rpc("wl_remove_tenant_extra_subscription" as never, {
        p_tenant_id: tenantId,
        p_assinatura_id: assinaturaId,
      } as never);
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: invalidate,
  });

  /**
   * extendTrial — prorroga o trial do tenant específico.
   * Aceita dois modos (v1.1.0 da RPC):
   *   - { newTrialEnd: ISO }   → data fixa de término (modo preferido do painel WL)
   *   - { additionalDays: N }  → soma N dias a partir do trial atual / agora
   * A trava absoluta (30 dias desde tenants.created_at) é aplicada no backend.
   * NÃO altera subscription_plans.trial_days (default global do plano).
   */
  const extendTrial = useMutation({
    mutationFn: async (
      input: number | { additionalDays: number } | { newTrialEnd: string },
    ) => {
      // Normaliza payload preservando retrocompat com chamada por número (dias)
      const rpcParams: Record<string, unknown> = { p_tenant_id: tenantId };
      if (typeof input === "number") {
        rpcParams.p_additional_days = input;
      } else if ("newTrialEnd" in input) {
        rpcParams.p_new_trial_end = input.newTrialEnd;
      } else {
        rpcParams.p_additional_days = input.additionalDays;
      }

      const { data, error } = await supabase.rpc(
        "wl_extend_tenant_trial" as never,
        rpcParams as never,
      );
      if (error) throw error;
      return data as {
        binding_id: string;
        tenant_id: string;
        input_mode: "date" | "days";
        additional_days: number | null;
        previous_trial_end: string | null;
        new_trial_end: string;
        max_allowed_trial_end: string;
        status: string;
      };
    },
    onSuccess: invalidate,
  });

  return { applyPlan, addExtra, removeExtra, extendTrial };
}
